<template>
  <v-dialog
    class='leak-date-filter-popup'
    :value="visiblePopup"
    :max-width="400"
    @click:outside="onNoClickButton()"
    @keydown.esc="onNoClickButton()"
  >
    <v-card class="popup-wrapper">
      <div class="title">{{$t(title)}}</div>
      <div class="date-tabs">
        <el-radio-group v-model="activeTab" size="small">
          <el-radio-button
            class="tab-button"
            v-for="tab in tabs"
            :label="tab.label"
            :key="tab.label"
          />
        </el-radio-group>
      </div>
      <div class="beforeTab" v-show="isBeforeTab">
        <div class='leak-date-wrapper'>
          <custom-datepicker
            v-model="beforeSelectedDate"
            type="date"
            format="DD MMM YYYY"
            :title="$t('before.date').toString()"
          />
        </div>
      </div>
      <div class="beforeTab" v-show="isBetweenTab">
        <div class='leak-date-wrapper'>
          <custom-datepicker
            v-model="betweenSelectedDate"
            type="daterange"
            format="DD MMM YYYY"
            range-separator="To"
            start-placeholder="Start day"
            end-placeholder="End day"
            :title="$t('between.date').toString()"
          />
        </div>
      </div>
      <div class="beforeTab" v-show="isAfterTab">
        <div class='leak-date-wrapper'>
          <custom-datepicker
            v-model="afterSelectedDate"
            type="date"
            format="DD MMM YYYY"
            :title="$t('after.date').toString()"
          />
        </div>
      </div>
      <div class="buttons">
        <v-btn v-if='showNoButton' small depressed class="no-button btn-secondary" @click="onNoClickButton()">
          {{$t(noCaption)}}
        </v-btn>
        <v-btn small depressed class="yes-button btn-primary" @click="onYesClickButton()">
          {{$t(yesCaption)}}
        </v-btn>
      </div>
    </v-card>
  </v-dialog>

</template>

<script>
import disableOuterScrollOnPopupVisible from '@/mixins/disableOuterScrollOnPopupVisible';
import moment from 'moment';
import CustomDatepicker from '@/components/datepicker/CustomDatepicker.vue';

export default {
  name: 'SelectLeakDateFilterPopup',
  mixins: [disableOuterScrollOnPopupVisible],
  components: { CustomDatepicker },
  props: {
    title: String,
    yesCaption: String,
    noCaption: String,
    showNoButton: {
      type: Boolean,
      default: true,
      required: false,
    },
    visiblePopup: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      dateUpdatedValue: {
        from: null,
        to: null,
      },
      beforeSelectedDate: new Date(),
      betweenSelectedDate: [new Date(), new Date()],
      afterSelectedDate: new Date(),
      tab: this.$t('last.scanned.date.popup.before'),
    };
  },
  watch: {
    visiblePopup() {
      this.beforeSelectedDate = new Date();
      this.betweenSelectedDate = [new Date(), new Date()];
      this.afterSelectedDate = new Date();
    },
  },
  methods: {
    onYesClickButton() {
      if (this.isBeforeTab) {
        this.dateUpdatedValue.to = moment(this.beforeSelectedDate).format('DD MMM YYYY');
        this.dateUpdatedValue.from = null;
      } else if (this.isAfterTab) {
        this.dateUpdatedValue.from = moment(this.afterSelectedDate).format('DD MMM YYYY');
        this.dateUpdatedValue.to = null;
      } else if (this.isBetweenTab) {
        this.dateUpdatedValue.from = moment(this.betweenSelectedDate[0]).format('DD MMM YYYY');
        this.dateUpdatedValue.to = moment(this.betweenSelectedDate[1]).format('DD MMM YYYY');
      }
      this.$emit('clicked', this.dateUpdatedValue);
    },
    onNoClickButton() {
      this.$emit('clicked', false);
    },
  },
  computed: {
    beforeTab() {
      return {
        label: this.$t('last.scanned.date.popup.before'),
      };
    },
    betweenTab() {
      return {
        label: this.$t('last.scanned.date.popup.between'),
      };
    },
    afterTab() {
      return {
        label: this.$t('last.scanned.date.popup.after'),
      };
    },
    isBeforeTab() {
      return this.activeTab === this.beforeTab.label;
    },
    isBetweenTab() {
      return this.activeTab === this.betweenTab.label;
    },
    isAfterTab() {
      return this.activeTab === this.afterTab.label;
    },
    tabs() {
      return [this.beforeTab, this.betweenTab, this.afterTab];
    },
    activeTab: {
      set(tab) {
        this.tab = tab;
      },
      get() {
        return this.tab;
      },
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/design-system';

.popup-wrapper {
  max-width: rem(400);
  min-width: rem(400);
  padding: $space-8 $space-10;

  .beforeTab {
    display: flex;
    justify-content: center;

    .leak-date-wrapper {
      width: rem(315);
      margin-bottom: $space-6;
      margin-top: $space-5;
    }
  }

  .title {
    color: var(--text-primary);
    font-size: $font-size-base;
    font-weight: 600;
    letter-spacing: 1.12px;
    line-height: $space-4;
    text-transform: uppercase;
    padding-bottom: $space-6;
  }

  .buttons {
    text-align: right;
    padding-top: $space-7;

    .no-button {
      box-sizing: border-box;
      height: $btn-height-md;
      width: rem(86);
      border: $border-width-thin solid var(--brand-primary);
      color: var(--brand-primary);
      border-radius: $radius-sm;
      background-color: var(--bg-primary);
      box-shadow: $shadow-sm;
    }

    .yes-button {
      height: $btn-height-md;
      width: rem(51);
      border-radius: $radius-sm;
      background-color: var(--brand-primary);
      box-shadow: $shadow-sm;
      color: var(--text-inverse);
      font-size: $font-size-sm;
      font-weight: 500;
      letter-spacing: 1.07px;
      line-height: $space-4;
      text-align: center;
      margin-left: $space-2;
    }
  }
}
</style>

<style lang="scss">
// Match cloud tab radio button style
.popup-wrapper .date-tabs {
  .el-radio-button__inner {
    color: #606266 !important;
  }

  .el-radio-button__inner:hover {
    color: var(--text-secondary) !important;
  }

  .el-radio-button__orig-radio:checked + .el-radio-button__inner {
    background-color: var(--text-secondary) !important;
    border-color: var(--text-secondary) !important;
    box-shadow: -1px 0 0 0 var(--text-secondary) !important;
    color: #ffffff !important;
  }
}
</style>
